// @flow
/** @jsx jsx */
import * as React from 'react';
import { css, jsx, ClassNames } from '@emotion/core';
import { Route, Link } from 'react-router-dom';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import Overlay from 'react-bootstrap/lib/Overlay';
import Popover from 'react-bootstrap/lib/Popover';
import Table from 'react-bootstrap/lib/Table';
import { CollectionView } from 'wijmo/wijmo';

import colors from '../config/colors';
import messages from '../config/messages';
import type { GridItem } from '../types';
import Base from "../layout/Base.layout";
import getActiveOrderQuery from "../graphql/queries/getActiveOrder.query";

const activeOrderActiveStyles = css`
  &::before {
    background: ${colors.blue};
  }
`;

const activeOrderInactiveStyles = css`
  &::before {
    background: ${colors.gray};
  }
`;

const orderButtonStyles = css`
  &::before {
    border-radius: 3px;
    position: absolute;
    width: 80px;
    height: 40px;
    content: '';
    top: -10px;
    left: -10px;
  }
`;

type MenuActiveOrderItemProps = {
  active: boolean,
  Account: any,
  OrderType: string,
  FlatDet: Array<GridItem>,
  items?: CollectionView,
  hasUnsavedOrderType: boolean,
  isLoading: boolean,
};

type MenuActiveOrderItemState = {
  show: boolean,
};

export default class MenuActiveOrderItem extends React.Component<
  MenuActiveOrderItemProps,
  MenuActiveOrderItemState,
> {
  state = {
    show: false,
  };

  portal = document.getElementById('portal');

  menuTrigger = React.createRef();
  menuContainer = React.createRef();

  componentDidMount() {
    document.addEventListener('click', this.hideMenu, false);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.hideMenu);
  }

  hideMenu = (e: any) => {
    // $FlowFixMe
    if (
      (this.menuContainer && this.menuContainer.current.contains(e.target)) ||
      this.portal.contains(e.target)
    ) {
      return;
    }

    this.setState({
      show: false,
    });
  };

  handleToggleMenu = (e: any) => {
    Base.getClient().query({query: getActiveOrderQuery})

    this.setState(state => ({
      show: !state.show,
    }));
  };

  render() {
    return (
      <Route
        path="/order"
        exact
        children={({ match }) => {
          return (
            <ClassNames>
              {({ cx }) => (
                <li
                  css={{
                    position: 'relative',
                    display: 'flex !important',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '60px',
                    minWidth: '100px',
                  }}
                  className={cx({
                    'is-active': !!match,
                  })}
                  ref={this.menuContainer}
                >
                  <div
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {this.props.active && this.props.Account && (
                      <ActiveOrderAccountIndicator
                        title={this.props.Account.AcctName}
                      />
                    )}
                    <div
                      css={[
                        {
                          position: 'relative',
                          cursor: 'pointer',
                          color: colors.white,
                        },
                        orderButtonStyles,
                        this.props.active
                          ? activeOrderActiveStyles
                          : activeOrderInactiveStyles,
                      ]}
                      onClick={this.handleToggleMenu}
                      ref={this.menuTrigger}
                    >
                      <div
                        css={{
                          position: 'relative',
                          zIndex: 1,
                        }}
                      >
                        <Glyphicon glyph="shopping-cart" /> Order
                      </div>
                    </div>
                  </div>

                  <Overlay
                    show={this.state.show}
                    placement="bottom"
                    container={this.portal}
                    target={this.menuTrigger.current}
                  >
                    <Popover
                      id="popover-active-order"
                      title={
                        this.props.active && this.props.Account ? (
                          <ActiveOrderItemTitle
                            title={this.props.Account.AcctName}
                            onClick={this.handleToggleMenu}
                          />
                        ) : (
                          ''
                        )
                      }
                      css={{
                        minWidth: '450px',
                      }}
                    >
                      <ActiveOrderContent {...this.props}/>
                    </Popover>
                  </Overlay>
                </li>
              )}
            </ClassNames>
          );
        }}
      />
    );
  }
}

const ActiveOrderContent = (props: MenuActiveOrderItemProps) => {
  if (props.active) {
    /**
     * This is basically giving precedence to the local collection over the
     * one coming from server. This is required only when the user changed
     * the order type and didn't save the changes
     */
    // const orderItems =
    //   (props.hasUnsavedOrderType && props.items) || props.FlatDet;
    const orderItems = props.FlatDet;

    const showOrderDetails =
      orderItems && (orderItems.totalItemCount || orderItems.length > 0);

    return (
      <React.Fragment>
        <p>
          <strong>Type: </strong> {props.OrderType}{' '}
          {props.hasUnsavedOrderType && (
            <em
              css={{
                color: 'red',
              }}
              title="Click on 'Save order' to persist the changes"
            >
              (Unsaved)
            </em>
          )}
        </p>
        <p>
          <strong>Account #: </strong> {props.Account.AcctNo}
        </p>
        {props.Account.Address && (
          <p>
            <strong>Account Address: </strong> {props.Account.Address}
          </p>
        )}
        <React.Fragment>
          <p>
            <strong>Order Details:</strong>
          </p>
          <Table
            striped
            bordered
            condensed
            hover
            css={{
              marginBottom: 0,
            }}
          >
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th>Cases</th>
                <th>Bottles</th>
              </tr>
            </thead>
            <tbody>
              {props.isLoading ? (
                <tr>
                  <td
                    colSpan="4"
                    css={{
                      color: colors.black,
                      textAlign: 'center',
                    }}
                  >
                    Updating...
                  </td>
                </tr>
              ) : showOrderDetails ? (
                orderItems.map(item =>
                  item.Cases > 0 || item.Bottles > 0 ? (
                    <tr key={item.Code}>
                      <td>{item.Code}</td>
                      <td>{item.Description}</td>
                      <td>{item.Cases}</td>
                      <td>{item.Bottles}</td>
                    </tr>
                  ) : null,
                )
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    css={{
                      color: colors.black,
                      textAlign: 'center',
                    }}
                  >
                    No items.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </React.Fragment>
      </React.Fragment>
    );
  }

  return (
    <p
      css={{
        marginBottom: 0,
      }}
    >
      {messages.common.orderNoActive}
    </p>
  );
};

const ActiveOrderAccountIndicator = (props: { title: string }) => {
  return (
    <div
      css={{
        display: 'flex !important',
        alignItems: 'center',
        transform: 'translateX(-100%)',
        position: 'absolute',
        left: '10px',
        cursor: 'pointer',
      }}
    >
      <span
        css={{
          background: colors.lightEmerald,
          padding: '5px 10px',
          color: colors.emerald,
          whiteSpace: 'nowrap',
          marginRight: '-1px',
        }}
      >
        {props.title}
      </span>
      <svg height="10" width="10">
        <polygon points="5,5 0,0 0,10" fill={colors.lightEmerald} />
      </svg>
    </div>
  );
};

const ActiveOrderItemTitle = ({
  title,
  onClick,
}: {
  title: string,
  onClick: (e: any) => mixed,
}) => {
  const commonProps = {
    css: {
      display: 'flex',
    },
  };

  if (window.location.pathname === '/order') {
    return <span {...commonProps}>{title}</span>;
  }

  return (
    <span {...commonProps}>
      {title}
      <Link
        css={{
          marginLeft: 'auto',
          minWidth: '90px',
          paddingLeft: '10px',
        }}
        to={`/order`}
        onClick={onClick}
      >
        View Order
      </Link>
    </span>
  );
};
